import { format } from 'date-fns/format';

import { normalizeDateRange } from '../../logical/date-value.js';
import { t } from '../../logical/type-presets.js';
import { createFilter } from './create.js';

const day = (value: number) => format(value, 'yyyy/MM/dd');

/**
 * Filters that need both ends of the span, so they are declared on
 * `t.dateRange` and never show up for a plain `date` column (there is no
 * `Date -> DateRange` convert registered).
 *
 * Their arguments stay `t.date`, which means the existing `<date-picker>`
 * literal editor is reused as-is.
 */
export const dateRangeFilter = [
  createFilter({
    name: 'range-starts-after',
    self: t.dateRange.instance(),
    args: [t.date.instance()] as const,
    label: 'Starts after',
    shortString: v => (v ? ` starts > ${day(v.value)}` : undefined),
    impl: (self, value) => {
      const range = normalizeDateRange(self);
      return range != null && range.start > value;
    },
  }),
  createFilter({
    name: 'range-ends-before',
    self: t.dateRange.instance(),
    args: [t.date.instance()] as const,
    label: 'Ends before',
    shortString: v => (v ? ` ends < ${day(v.value)}` : undefined),
    impl: (self, value) => {
      const range = normalizeDateRange(self);
      return range != null && range.end < value;
    },
  }),
  createFilter({
    name: 'range-contains-date',
    self: t.dateRange.instance(),
    args: [t.date.instance()] as const,
    label: 'Contains date',
    shortString: v => (v ? ` contains ${day(v.value)}` : undefined),
    impl: (self, value) => {
      const range = normalizeDateRange(self);
      return range != null && range.start <= value && range.end >= value;
    },
  }),
  createFilter({
    name: 'range-overlaps',
    self: t.dateRange.instance(),
    args: [t.date.instance(), t.date.instance()] as const,
    label: 'Overlaps period',
    shortString: (from, to) =>
      from && to
        ? ` overlaps ${day(from.value)} – ${day(to.value)}`
        : undefined,
    impl: (self, from, to) => {
      const range = normalizeDateRange(self);
      if (range == null) {
        return false;
      }
      const [start, end] = from <= to ? [from, to] : [to, from];
      return range.start <= end && range.end >= start;
    },
    defaultValue: args => ({
      start: args[0],
      end: Math.max(args[0], args[1]),
      hasTime: false,
    }),
  }),
];
